<!-- GENERATED by scripts/build-legal-pages.mjs from legal/src/security.md. Edit the source, then rebuild. -->
<template>
  <main class="sec-page">
    <header class="sec-hero">
      <div class="sec-wrap">
        <p class="sec-updated">Last Updated October 15, 2026</p>
        <h1>Security</h1>
        <p class="sec-lede">Workspace369 holds client records, invoices, messages and signed documents for small businesses. This page says what we do to protect them, what we do not claim, and how to report a problem.</p>
        <nav aria-label="On this page"><ul class="sec-jump"><li><a href="#what-we-do">What we do</a></li><li><a href="#incident-response">Incident response</a></li><li><a href="#report-a-vulnerability">Report a vulnerability</a></li><li><a href="#what-we-do-not-claim">What we do not claim</a></li></ul></nav>
      </div>
    </header>
    <div class="sec-wrap sec-body">
        <section id="what-we-do" class="sec-section sec-section--cards" aria-labelledby="what-we-do-title">
          <h2 id="what-we-do-title">What we do</h2>
          <div class="sec-cards"><div class="sec-card"><h3>Hosting.</h3><p>Workspace369 runs on Google Cloud and Firebase, in the United States (database in South Carolina, application servers mostly in Iowa). Some AI requests may be served by Google's global endpoint, as our AI page explains. Google encrypts stored data at rest and traffic to and from our services uses HTTPS (TLS).</p></div><div class="sec-card"><h3>Payments.</h3><p>Card payments are processed by Stripe. We do not store full card numbers.</p></div><div class="sec-card"><h3>Sign-in and access.</h3><p>Phone-based multi-factor authentication is on by default, and the account holder can turn it off in account security settings. Roles (owner, admin, manager, member, viewer and custom roles) and per-screen permissions control who sees what. Contractors can be limited to the clients assigned to them. Our database access rules are written to deny anything not explicitly allowed.</p></div><div class="sec-card"><h3>Your data.</h3><p>The workspace owner can export the workspace and delete it. An export link works for 24 hours. A workspace deletion runs after a 24-hour grace period, and the owner can cancel before it ends. Some records are kept longer where the law or a contract needs it; for example sealed e-signature evidence is kept for its configured retention period (see the Privacy Policy).</p></div><div class="sec-card"><h3>Documents and signatures.</h3><p>Each signing event is chained to the one before it with SHA-256 hashes, so a changed record shows. Completed documents get a tamper-evident digital seal. Signing keys are held in a hardware-backed key service.</p></div><div class="sec-card"><h3>Inbound mail and files.</h3><p>Incoming email is checked for sender authentication (SPF, DKIM, DMARC), phishing and look-alike domains, and attachments are scanned for malware on Specialist, Commander and Enterprise plans. These are automated checks, not a guarantee.</p></div><div class="sec-card"><h3>Secrets.</h3><p>Service credentials are kept in Google Secret Manager, not in the app.</p></div><div class="sec-card"><h3>People.</h3><p>Access to production systems is limited to the founder and authorised contractors who need it, through Google Cloud access controls.</p></div></div>
        </section>
        <section id="incident-response" class="sec-section" aria-labelledby="incident-response-title">
          <h2 id="incident-response-title">Incident response</h2>
          <div class="sec-prose"><p class="my-5">If we confirm that personal data in your account was accessed or disclosed without authorization, we notify affected customers without undue delay, within 72 hours of confirming it for business customers (as stated in our DPA), and within the deadlines that apply by law, and we tell you what happened, what data was involved and what to do. Florida law, for example, sets a 30-day outer limit for notices to individuals. Where we process data on behalf of a business customer, we tell that customer promptly so they can meet their own duties.</p></div>
        </section>
        <section id="report-a-vulnerability" class="sec-section sec-section--panel" aria-labelledby="report-a-vulnerability-title">
          <h2 id="report-a-vulnerability-title">Report a vulnerability</h2>
          <div class="sec-prose"><p class="my-5">If you think you found a security problem in Workspace369, please tell us first.</p>
        <ul class="list-disc pl-6 space-y-3 my-5"><li>Email <strong><a href="mailto:support@workspace369.com" class="text-[#0e7490] dark:text-[#67e8f9] underline">support@workspace369.com</a></strong> with the subject "Security report" (machine-readable details: <a href="/.well-known/security.txt" class="text-[#0e7490] dark:text-[#67e8f9] underline">/.well-known/security.txt</a>).</li><li>Include what you found, the URL or API, steps to reproduce, and any proof-of-concept. Please do not include real customer data.</li><li>We acknowledge reports within 3 business days and aim to give you a status update within 10 business days.</li></ul>
        <p class="my-5">When you test, please:</p>
        <ul class="list-disc pl-6 space-y-3 my-5"><li>Only use accounts you own or have permission to use. Do not access, change or delete other people's data, and stop as soon as you see real customer data.</li><li>Do not run denial-of-service tests, spam, social engineering or physical attacks.</li><li>Give us reasonable time to fix the issue before you share it.</li></ul>
        <p class="my-5">If you follow these rules and act in good faith, we will not take legal action against you for your research, and we will work with you to fix the problem. We do not currently run a paid bug bounty, but we are glad to credit you if you wish.</p></div>
        </section>
        <section id="what-we-do-not-claim" class="sec-section" aria-labelledby="what-we-do-not-claim-title">
          <h2 id="what-we-do-not-claim-title">What we do not claim</h2>
          <div class="sec-prose"><ul class="list-disc pl-6 space-y-3 my-5"><li>Workspace369 does not currently hold SOC 2, ISO 27001 or similar certifications.</li><li>Workspace369 is not designed for protected health information (PHI) or payment-card data beyond what Stripe handles. Our Terms forbid storing PHI.</li><li>No system is perfectly secure. We cannot promise that a breach will never happen.</li></ul></div>
        </section>
        <section id="related" class="sec-section" aria-labelledby="related-title">
          <h2 id="related-title">Related</h2>
          <ul class="sec-links"><li><a href="/privacy-policy/">Privacy Policy</a></li><li class="sec-sep" aria-hidden="true">·</li><li><a href="/terms-of-service/">Terms of Service</a></li><li class="sec-sep" aria-hidden="true">·</li><li><a href="/trust/">Trust</a></li><li class="sec-sep" aria-hidden="true">·</li><li><a href="/ai/">How we use AI</a></li><li class="sec-sep" aria-hidden="true">·</li><li><a href="/dmca/">Copyright policy</a></li><li class="sec-sep" aria-hidden="true">·</li><li><a href="/sub-processors/">Sub-processors</a></li></ul>
        </section>
      <section class="sec-cta" aria-labelledby="sec-cta-title">
        <h2 id="sec-cta-title">Security questions before you buy?</h2>
        <p>Send them to sales and say what your review needs. The DPA and the sub-processors list are public.</p>
        <div class="sec-cta-links"><a href="/contact-sales/">Contact sales</a><a href="/dpa/">Read the DPA</a><a href="/sub-processors/">Sub-processors</a></div>
      </section>
      <nav class="sec-legal" aria-label="Legal documents">
        <h2>Legal documents</h2>
        <ul><li><a href="/legal/" >Legal documents index</a></li><li><a href="/terms-of-service/" >Terms of Service</a></li><li><a href="/privacy-policy/" >Privacy Policy</a></li><li><a href="/acceptable-use/" >Acceptable Use Policy</a></li><li><a href="/refund-policy/" >Refund and Cancellation Policy</a></li><li><a href="/dpa/" >Data Processing Addendum (DPA)</a></li><li><a href="/sub-processors/" >Sub-processors</a></li><li><a href="/sms-terms/" >SMS Terms</a></li><li><a href="/ai/" >How we use AI</a></li><li><a href="/dmca/" >Copyright policy (DMCA)</a></li><li><a href="/accessibility/" >Accessibility</a></li><li><a href="/delete-account/" >Delete your account</a></li><li><a href="/cookie-policy/" >Cookie Policy</a></li><li><a href="/trust/" >Trust</a></li></ul>
      </nav>
    </div>
  </main>
</template>

<script setup>
useHead({
  title: "Security | Workspace369",
  meta: [
    { name: 'description', content: "What Workspace369 does to protect client records, invoices, messages and signed documents, what we do not claim, and how to report a vulnerability." },
    { name: 'twitter:title', content: "Security | Workspace369" },
    { name: 'twitter:description', content: "What Workspace369 does to protect client records, invoices, messages and signed documents, what we do not claim, and how to report a vulnerability." },
    { name: 'twitter:card', content: 'summary_large_image' },
    { property: 'og:title', content: "Security | Workspace369" },
    { property: 'og:description', content: "What Workspace369 does to protect client records, invoices, messages and signed documents, what we do not claim, and how to report a vulnerability." }
  ]
});
</script>

<style scoped>
.sec-page {
  --sec-bg: #ffffff;
  --sec-soft: #f6f9fc;
  --sec-ink: #0f172a;
  --sec-muted: #4b5a6d;
  --sec-line: #dde6ef;
  --sec-card: #ffffff;
  --sec-accent: #0e7490;
  --sec-accent-soft: #ecf7fb;
  background: var(--sec-bg);
  color: var(--sec-ink);
}
.dark .sec-page {
  --sec-bg: #020617;
  --sec-soft: #0b1220;
  --sec-ink: #f1f5f9;
  --sec-muted: #a5b4c6;
  --sec-line: #1f2b3d;
  --sec-card: #0b1220;
  --sec-accent: #67e8f9;
  --sec-accent-soft: #0b2230;
}
.sec-wrap {
  max-width: 72rem;
  margin: 0 auto;
  padding: 0 1rem;
}
.sec-hero {
  background: radial-gradient(circle at 12% 10%, rgb(27 167 206 / 0.16), transparent 30rem), linear-gradient(180deg, #020617 0%, #0b1220 100%);
  color: #fff;
  padding: 7.25rem 0 2.5rem;
}
.sec-updated {
  color: #7dd3fc;
  font-size: 0.85rem;
  font-weight: 700;
}
.sec-hero h1 {
  margin-top: 0.6rem;
  color: #fff;
  font-size: clamp(2.4rem, 5vw, 3.6rem);
  font-weight: 850;
  line-height: 1.05;
}
.sec-lede {
  margin-top: 1rem;
  max-width: 42rem;
  color: #cbd5e1;
  font-size: 1.05rem;
  line-height: 1.65;
}
.sec-jump {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  margin-top: 1.75rem;
}
.sec-page a:focus-visible {
  outline: 2px solid var(--sec-accent);
  outline-offset: 2px;
}
.sec-hero a:focus-visible,
.sec-cta a:focus-visible {
  outline-color: #7dd3fc;
}
.sec-jump a {
  display: inline-flex;
  min-height: 2.25rem;
  align-items: center;
  border: 1px solid rgb(148 163 184 / 0.32);
  border-radius: 0.3rem;
  padding: 0.35rem 0.75rem;
  color: #e2e8f0;
  font-size: 0.84rem;
  font-weight: 700;
}
.sec-jump a:hover {
  border-color: #7dd3fc;
  color: #fff;
}
.sec-body {
  padding-top: 1rem;
  padding-bottom: 4rem;
}
.sec-section {
  border-top: 1px solid var(--sec-line);
  padding: 2.25rem 0;
  scroll-margin-top: 5rem;
}
.sec-section:first-child {
  border-top: 0;
}
.sec-section > h2 {
  color: var(--sec-ink);
  font-size: clamp(1.4rem, 2.6vw, 1.9rem);
  font-weight: 850;
  line-height: 1.2;
}
.sec-cards {
  display: grid;
  gap: 0.75rem;
  margin-top: 1.25rem;
}
.sec-card {
  border: 1px solid var(--sec-line);
  border-radius: 0.5rem;
  background: var(--sec-card);
  padding: 1rem 1.1rem;
}
.sec-card h3 {
  color: var(--sec-ink);
  font-size: 1rem;
  font-weight: 800;
}
.sec-card p {
  margin-top: 0.4rem;
  color: var(--sec-muted);
  font-size: 0.92rem;
  line-height: 1.6;
}
.sec-prose {
  max-width: 46rem;
  color: var(--sec-ink);
  font-size: 0.98rem;
  line-height: 1.7;
}
.sec-prose :deep(p) {
  margin: 1rem 0;
}
.sec-prose :deep(ul) {
  margin: 1rem 0;
  padding-left: 1.25rem;
  list-style: disc;
}
.sec-prose :deep(li) {
  margin-top: 0.6rem;
}
.sec-prose :deep(li::marker) {
  color: var(--sec-accent);
}
.sec-section--panel {
  border-top: 0;
}
.sec-section--panel + .sec-section {
  border-top: 0;
}
.sec-section--panel {
  margin: 1rem 0;
  border: 1px solid var(--sec-line);
  border-radius: 0.5rem;
  background: var(--sec-accent-soft);
  padding: 1.5rem 1.25rem;
}
.sec-links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
}
.sec-sep {
  display: inline-flex;
  align-items: center;
  color: var(--sec-muted);
}
.sec-links a {
  display: inline-flex;
  min-height: 2.5rem;
  align-items: center;
  border: 1px solid var(--sec-line);
  border-radius: 0.375rem;
  background: var(--sec-card);
  padding: 0.45rem 0.85rem;
  color: var(--sec-ink);
  font-size: 0.9rem;
  font-weight: 700;
}
.sec-links a:hover {
  border-color: var(--sec-accent);
}
.sec-cta {
  margin-top: 1rem;
  border-radius: 0.6rem;
  background: linear-gradient(180deg, #020617 0%, #0b1220 100%);
  padding: 2rem 1.25rem;
  color: #fff;
}
.dark .sec-cta {
  border: 1px solid var(--sec-line);
}
.sec-cta h2 {
  color: #fff;
  font-size: clamp(1.4rem, 2.6vw, 1.9rem);
  font-weight: 850;
  line-height: 1.2;
}
.sec-cta p {
  margin-top: 0.6rem;
  max-width: 40rem;
  color: #cbd5e1;
  line-height: 1.6;
}
.sec-cta-links {
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 1.25rem;
}
.sec-cta-links a {
  display: inline-flex;
  min-height: 2.9rem;
  align-items: center;
  border: 1px solid rgb(255 255 255 / 0.22);
  border-radius: 0.375rem;
  padding: 0.7rem 1.1rem;
  color: #fff;
  font-size: 0.92rem;
  font-weight: 800;
}
.sec-cta-links a:first-child {
  border-color: #0e7490;
  background: #0e7490;
}
.sec-cta-links a:first-child:hover {
  background: #0b6a84;
}
.sec-legal {
  margin-top: 3rem;
  border-top: 1px solid var(--sec-line);
  padding-top: 1.5rem;
}
.sec-legal h2 {
  color: var(--sec-ink);
  font-size: 1rem;
  font-weight: 800;
}
.sec-legal ul {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.4rem 1rem;
  margin-top: 0.75rem;
  font-size: 0.88rem;
}
.sec-legal a {
  color: var(--sec-muted);
}
.sec-legal a:hover {
  color: var(--sec-accent);
  text-decoration: underline;
}
@media (min-width: 640px) {
  .sec-wrap {
    padding: 0 1.5rem;
  }
  .sec-cards {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .sec-legal ul {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  .sec-cta {
    padding: 2.5rem 2rem;
  }
}
@media (min-width: 1024px) {
  .sec-hero {
    padding: 9rem 0 3.25rem;
  }
  .sec-section {
    display: grid;
    grid-template-columns: minmax(13rem, 0.32fr) minmax(0, 1fr);
    gap: 2.5rem;
  }
  .sec-section > h2 {
    font-size: 1.5rem;
  }
  .sec-section--panel {
    padding: 2rem;
  }
  .sec-prose > :deep(:first-child),
  .sec-links {
    margin-top: 0;
  }
  .sec-cards {
    margin-top: 0;
  }
  .sec-cta {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 0.5rem 2rem;
  }
  .sec-cta h2,
  .sec-cta p {
    grid-column: 1;
  }
  .sec-cta-links {
    grid-column: 2;
    grid-row: 1 / span 2;
    margin-top: 0;
  }
}
</style>
